import React from "react";
import Dropzone from "react-dropzone";
import { connect } from "react-redux";
import ReactCrop from "react-image-crop";
import { Button } from "reactstrap";
import ModalFrame from "../../../toolkit/modalFrame";
import withStyles from "isomorphic-style-loader/withStyles";
import cropStyle from "react-image-crop/dist/ReactCrop.css";
import s from "./styles.scss";
import cropUi from "../shared/imageCrop.scss";
import {
  CROP_PRESETS,
  CropPresetId,
  cropForPreset,
  presetIconClass,
  scaleCropDimensions,
} from "../../utils/imageCropPresets";
import {
  ModalProps,
  serviceCaller,
  ServiceCallerProps,
  withModals,
} from "../../composition";
import { firebaseConnect } from "react-redux-firebase";
import Fragment from "../../../toolkit/fragment";
import { Col, Row } from "reactstrap";
import get from "lodash.get";
import ICharityConfig from "../../../types/ICharityConfig";

interface Props extends ServiceCallerProps, ModalProps {
  isOpen: boolean;
  toggle: (blob?: Blob) => void;
  onlyImage: boolean;
  thumbnailBlob?: Blob;
  charityConfig: ICharityConfig;
}

interface State {
  dropped: boolean;
  currentThumbnailBlobUrl?: string;
  currentThumbnailBlob?: Blob;
  cropImage: HTMLImageElement;
  crop?: any;
  pixelCrop?: any;
  thumbnailChosen: boolean;
  cropPreset: CropPresetId;
}

class CustomThumbnailModal extends React.Component<Props, State> {
  static defaultProps = {
    onlyImage: false,
  };

  state = {
    dropped: false,
    currentThumbnailBlobUrl: null,
    currentThumbnailBlob: null,
    cropImage: null,
    crop: {
      x: 0,
      y: 0,
      width: 100,
      height: 100,
    },
    pixelCrop: {
      x: 0,
      y: 0,
      width: 0,
      height: 0,
    },
    thumbnailChosen: false,
    cropPreset: "original" as CropPresetId,
  };

  onDrop = (accepted: Array<File>) => {
    accepted.forEach(file => {
      const blobUrl = URL.createObjectURL(file);
      this.setState({
        dropped: true,
        currentThumbnailBlobUrl: blobUrl,
        currentThumbnailBlob: file,
        cropPreset: "original",
      });
    });
  };

  onCropImageLoaded = (image: HTMLImageElement) => {
    this.setState({
      cropImage: image,
      crop: cropForPreset(this.state.cropPreset, image),
    });
  };

  onCropPresetChange = (presetId: CropPresetId) => {
    const image = this.state.cropImage;
    if (!image) {
      this.setState({ cropPreset: presetId });
      return;
    }
    this.setState({
      cropPreset: presetId,
      crop: cropForPreset(presetId, image),
    });
  };

  onCropChange = (crop, pixelCrop) => {
    this.setState({ crop: crop, pixelCrop: pixelCrop });
  };

  returnToDropzone = () => {
    this.setState({
      currentThumbnailBlobUrl: null,
      currentThumbnailBlob: null,
      dropped: false,
      thumbnailChosen: false,
      cropPreset: "original",
    });
  };

  finish = () => {
    const resultImage: Promise<HTMLImageElement> = new Promise(resolve =>
      resolve(this.state.cropImage)
    );

    this.setState({
      thumbnailChosen: true,
    });

    const maxWidth = get(this.props.charityConfig, "maxImageSize", 1000);
    const finalCanvas = document.createElement("canvas");
    const finalContext = finalCanvas.getContext("2d");

    resultImage.then(image => {
      const sourceWidth = (this.state.crop.width / 100) * image.naturalWidth;
      const sourceHeight =
        (this.state.crop.height / 100) * image.naturalHeight;

      const scaled = scaleCropDimensions(sourceWidth, sourceHeight, maxWidth);
      finalCanvas.width = scaled.width;
      finalCanvas.height = scaled.height;

      finalContext.drawImage(
        image,
        (this.state.crop.x / 100) * image.naturalWidth,
        (this.state.crop.y / 100) * image.naturalHeight,
        sourceWidth,
        sourceHeight,
        0,
        0,
        finalCanvas.width,
        finalCanvas.height
      );

      finalCanvas.toBlob(
        blob => {
          this.props.toggle(blob);
        },
        "image/jpeg",
        10
      );
    });
  };

  applyThumbnailBlob = (thumbnailBlob?: Blob) => {
    if (thumbnailBlob) {
      this.setState({
        currentThumbnailBlob: thumbnailBlob,
        currentThumbnailBlobUrl: URL.createObjectURL(thumbnailBlob),
        dropped: true,
        cropPreset: "original",
      });
    } else {
      this.returnToDropzone();
    }
  };

  componentDidMount() {
    this.applyThumbnailBlob(this.props.thumbnailBlob);
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.thumbnailBlob !== this.props.thumbnailBlob) {
      this.applyThumbnailBlob(this.props.thumbnailBlob);
    }

    if (
      !this.props.isOpen &&
      !this.state.thumbnailChosen &&
      this.state.currentThumbnailBlobUrl !== null
    ) {
      this.returnToDropzone();
    }
  }

  render() {
    const { onlyImage } = this.props;
    const accept = onlyImage
      ? ["image/jpeg", "image/png"]
      : ["image/jpeg", "image/png"];
    const dropzoneText =
      " Click here to select your photo or drop it here. Accepts JPEG or PNG images.";

    return (
      <ModalFrame
        isOpen={this.props.isOpen}
        toggle={() => this.props.toggle()}
        size="xl"
        title={"Select Thumbnail"}
      >
        {!this.state.dropped && (
          <div>
            <Fragment>
              <Dropzone
                id={"modalDrop"}
                className={s.dropzone}
                onDrop={this.onDrop}
                accept={accept}
                multiple={false}
              >
                <div className={s.dropzoneContent}>
                  <p>
                    <span>{dropzoneText}</span>
                  </p>
                </div>
              </Dropzone>
            </Fragment>
          </div>
        )}
        {this.state.dropped && (
          <div>
            {this.state.currentThumbnailBlobUrl && (
              <div className={cropUi.imagePreviewWrap}>
                {this.state.currentThumbnailBlob && (
                  <div>
                    <div className={cropUi.aspectPresets}>
                      {CROP_PRESETS.map(preset => (
                        <button
                          key={preset.id}
                          type="button"
                          title={preset.label}
                          aria-label={preset.label}
                          className={
                            this.state.cropPreset === preset.id
                              ? cropUi.aspectPresetActive
                              : cropUi.aspectPreset
                          }
                          onClick={() => this.onCropPresetChange(preset.id)}
                        >
                          <span
                            className={presetIconClass(preset.id, cropUi)}
                            aria-hidden="true"
                          />
                        </button>
                      ))}
                    </div>
                    <div className={cropUi.cropPreview}>
                      <ReactCrop
                        id={"thumbnailCrop"}
                        imageStyle={{
                          imageOrientation: "from-image",
                        }}
                        src={this.state.currentThumbnailBlobUrl}
                        crop={this.state.crop}
                        onChange={this.onCropChange}
                        onImageLoaded={this.onCropImageLoaded}
                      />
                    </div>
                  </div>
                )}
                {!this.state.currentThumbnailBlob && (
                  <div className={cropUi.cropPreview}>
                    <img
                      src={this.state.currentThumbnailBlobUrl}
                      className={cropUi.noCrop}
                      alt=""
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {(this.state.currentThumbnailBlobUrl || this.props.thumbnailBlob) && (
          <Row className={cropUi.cropActions}>
            <Col lg={{ size: 6 }} style={{ textAlign: "left" }}>
              <Button
                color="none"
                className={s.whiteButton}
                onClick={this.returnToDropzone}
              >
                Choose another file
              </Button>
            </Col>
            <Col lg={{ size: 6 }} style={{ textAlign: "right" }}>
              <Button className={s.whiteButton} onClick={this.finish}>
                Choose as thumbnail
              </Button>
            </Col>
          </Row>
        )}
      </ModalFrame>
    );
  }
}

const mapStateToProps = state => {
  const { modals, user, firebase } = state;
  const config = get(firebase, `data.${user.currentCharity.id}.config`);
  return {
    onlyImage: modals.onlyImage,
    thumbnailBlob: modals.thumbnailBlob,
    charityConfig: config ? config : null,
  };
};

export default connect(mapStateToProps)(
  firebaseConnect((_, store) => {
    return store.getState().user.currentCharity.id !== ""
      ? [`${store.getState().user.currentCharity.id}/config`]
      : [];
  })(
    serviceCaller(
      withModals(
        withStyles(s, cropUi, cropStyle)(CustomThumbnailModal)
      )
    )
  )
);
